import { useState, type ReactNode } from 'react'
import { useNavigate } from 'react-router-dom'
import { useQueryClient } from '@tanstack/react-query'
import { Loader2 } from 'lucide-react'
import { toast } from 'sonner'
import { createSampleHousehold } from '@/api/sample'
import { deleteHousehold, fetchMyHouseholds, type HouseholdSummary } from '@/api/households'
import { currentHousehold } from '@/lib/currentHousehold'
import { track } from '@/lib/analytics'
import { ROUTES } from '@/routes/routes'
import { useCurrentHousehold } from '@/hooks/useCurrentHousehold'
import { useMyHouseholds } from '@/hooks/useMyHouseholds'
import { Button } from '@/components/ui/button'

const OVERLAY = 'fixed inset-0 z-50 flex items-center justify-center bg-black/30'
const CARD =
  'bg-[var(--fl-surface)] rounded-xl shadow-[rgba(0,0,0,0.15)_0px_8px_40px] w-full max-w-sm mx-4 p-6'

export interface UseSampleHousehold {
  /** The caller's sample household, if they have one. */
  sample: HouseholdSummary | undefined
  /** Creates the sample, switches into it and opens the Dashboard. */
  startCreate: () => void
  /** Opens the delete confirmation; confirming deletes the sample and leaves it. */
  startDelete: () => void
  /** Render once wherever the hook is used. */
  dialogs: ReactNode
  isCreating: boolean
}

export function useSampleHousehold(): UseSampleHousehold {
  const navigate = useNavigate()
  const queryClient = useQueryClient()
  const { householdId, switchHousehold } = useCurrentHousehold()
  const { data: households } = useMyHouseholds()
  const sample = households?.find((h) => h.isSample)

  const [isCreating, setIsCreating] = useState(false)
  const [confirmingDelete, setConfirmingDelete] = useState(false)
  const [isDeleting, setIsDeleting] = useState(false)

  function enterSample(id: string) {
    switchHousehold(id)
    navigate(ROUTES.APP.DASHBOARD)
  }

  async function create() {
    setIsCreating(true)
    try {
      const { id } = await createSampleHousehold()
      track('sample_created')
      await queryClient.invalidateQueries({ queryKey: ['currentUser'] })
      enterSample(id)
    } catch (err: unknown) {
      const res = (err as { response?: { status?: number; data?: { detail?: string } } })?.response
      if (res?.status === 409 && res.data?.detail === 'SAMPLE_HOUSEHOLD_EXISTS') {
        try {
          const fresh = await queryClient.fetchQuery({
            queryKey: ['myHouseholds'],
            queryFn: fetchMyHouseholds,
            staleTime: 0,
          })
          const existing = fresh.find((h) => h.isSample)
          if (existing) {
            enterSample(existing.id)
            return
          }
        } catch {
          // fall through to the error toast
        }
      }
      toast.error('Couldn’t create the sample household. Please try again.')
    } finally {
      setIsCreating(false)
    }
  }

  async function confirmDelete() {
    if (!sample) return
    setIsDeleting(true)
    try {
      await deleteHousehold(sample.id, sample.name)
      track('sample_deleted')
      setConfirmingDelete(false)
      const fresh = await queryClient.fetchQuery({
        queryKey: ['myHouseholds'],
        queryFn: fetchMyHouseholds,
        staleTime: 0,
      })
      const real = fresh.find((h) => !h.isSample && !h.archivedAt)
      if (real) {
        if (householdId === sample.id) {
          switchHousehold(real.id)
        } else {
          await queryClient.invalidateQueries({ queryKey: ['myHouseholds'] })
        }
      } else {
        currentHousehold.clear()
        queryClient.clear()
        navigate(ROUTES.APP.ONBOARDING, { replace: true })
      }
    } catch {
      toast.error('Couldn’t delete the sample household. Please try again.')
    } finally {
      setIsDeleting(false)
    }
  }

  const dialogs = (
    <>
      {isCreating && (
        <div className={OVERLAY}>
          <div
            role="status"
            aria-live="polite"
            className={`${CARD} flex items-center gap-3 text-[var(--fl-text-1)]`}
          >
            <Loader2 className="h-5 w-5 animate-spin text-[var(--fl-primary)]" aria-hidden />
            <span className="text-sm font-medium">Creating sample data…</span>
          </div>
        </div>
      )}
      {confirmingDelete && (
        <div className={OVERLAY}>
          <div role="dialog" aria-modal="true" className={CARD}>
            <p className="text-sm text-[var(--fl-text-1)] mb-4">
              Delete the sample household? This removes the Sharma family and all its sample data.
              Your own household isn't touched.
            </p>
            <div className="flex gap-3">
              <Button
                type="button"
                variant="outline"
                className="flex-1"
                onClick={() => setConfirmingDelete(false)}
                disabled={isDeleting}
              >
                Cancel
              </Button>
              <Button
                type="button"
                variant="destructive"
                className="flex-1"
                onClick={confirmDelete}
                disabled={isDeleting}
              >
                Delete sample household
              </Button>
            </div>
          </div>
        </div>
      )}
    </>
  )

  return {
    sample,
    startCreate: () => void create(),
    startDelete: () => setConfirmingDelete(true),
    dialogs,
    isCreating,
  }
}
